import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Form, Badge, Spinner, Nav, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import useAuthStore, { isSuperAdminUser } from '../store/useAuthStore';
import apiClient from '../api';

export const openSuperAdminModal = () => {
  window.dispatchEvent(new CustomEvent('open-superadmin-impersonator'));
};

const SuperAdminImpersonator = () => {
  const {
    user,
    isAuthenticated,
    originalAdminUser,
    isImpersonating,
    startImpersonation,
    stopImpersonation,
  } = useAuthStore();

  const [showModal, setShowModal] = useState(false);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const [activeTab, setActiveTab] = useState('providers');
  const [searchQuery, setSearchQuery] = useState('');
  const [targets, setTargets] = useState({
    providers: [],
    drivers: [],
    clients: [],
    users: [],
  });

  const isSuperAdmin = useMemo(
    () => isAuthenticated && isSuperAdminUser(user, originalAdminUser),
    [isAuthenticated, user, originalAdminUser]
  );

  const fetchTargets = useCallback(async () => {
    setLoadingTargets(true);
    try {
      const res = await apiClient.get('/transport/superadmin/impersonation-targets');
      if (res.data) {
        setTargets({
          providers: res.data.providers || [],
          drivers: res.data.drivers || [],
          clients: res.data.clients || [],
          users: res.data.users || [],
        });
      }
    } catch (err) {
      console.error('Error cargando perfiles de Super Cuenta:', err);
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar los perfiles');
    } finally {
      setLoadingTargets(false);
    }
  }, []);

  useEffect(() => {
    const handleOpen = () => {
      if (!isSuperAdmin) return;
      setShowModal(true);
      fetchTargets();
    };
    window.addEventListener('open-superadmin-impersonator', handleOpen);
    return () => window.removeEventListener('open-superadmin-impersonator', handleOpen);
  }, [isSuperAdmin, fetchTargets]);

  if (!isSuperAdmin) {
    return null;
  }

  const handleOpenModal = () => {
    setShowModal(true);
    fetchTargets();
  };

  const handleSelectTarget = async (item) => {
    const key = `${item.target_type}_${item.id}`;
    setSwitchingId(key);
    try {
      const res = await apiClient.post('/transport/superadmin/impersonate', {
        target_type: item.target_type,
        target_id: item.id,
      });
      const { access_token, user: impUser, redirect_path } = res.data;
      startImpersonation(access_token, impUser);
      setShowModal(false);
      toast.success(`👁️ Viendo como: ${impUser.impersonation_label || impUser.name}`);
      window.location.href = redirect_path || '/transport';
    } catch (err) {
      console.error('Error al activar modo Ver Como:', err);
      toast.error(err?.response?.data?.detail || 'Error al cambiar de perfil');
    } finally {
      setSwitchingId(null);
    }
  };

  const handleReturnToSuperAdmin = () => {
    const restored = stopImpersonation();
    if (restored) {
      toast.info('🔙 Has vuelto a tu Super Cuenta de Administrador');
      window.location.href = '/transport';
    }
  };

  const currentList = targets[activeTab] || [];
  const filteredList = currentList.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      (item.name || '').toLowerCase().includes(q) ||
      (item.subtitle || '').toLowerCase().includes(q) ||
      (item.badge || '').toLowerCase().includes(q) ||
      (item.email || '').toLowerCase().includes(q) ||
      String(item.id).includes(q)
    );
  });

  const activeImpersonation = Boolean(isImpersonating || user?.impersonated_by);

  return (
    <>
      {/* Banner superior persistente cuando Alejandro está viendo la app como otro perfil */}
      {activeImpersonation && (
        <div
          style={{
            background: 'linear-gradient(90deg, #0f172a 0%, #1e1b4b 50%, #311042 100%)',
            color: '#f8fafc',
            padding: '8px 16px',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '10px',
            borderBottom: '2px solid #f59e0b',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            position: 'sticky',
            top: 0,
            zIndex: 2000,
          }}
        >
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span
              className="badge"
              style={{
                backgroundColor: '#f59e0b',
                color: '#0f172a',
                fontWeight: 800,
                fontSize: '0.75rem',
                letterSpacing: '0.4px',
              }}
            >
              👁️ SUPER CUENTA ACTIVA
            </span>
            <span className="text-white-50">Viendo exactamente la pantalla de:</span>
            <strong className="text-warning" style={{ fontSize: '0.92rem' }}>
              {user?.impersonation_label || user?.name || user?.email}
            </strong>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              size="sm"
              variant="outline-light"
              className="fw-bold py-1 px-3"
              style={{ fontSize: '0.78rem', borderRadius: '8px' }}
              onClick={handleOpenModal}
            >
              🔄 Cambiar Perfil
            </Button>
            <Button
              size="sm"
              variant="warning"
              className="fw-bold text-dark py-1 px-3 shadow-sm"
              style={{ fontSize: '0.78rem', borderRadius: '8px' }}
              onClick={handleReturnToSuperAdmin}
            >
              🔙 Volver a mi Super Cuenta
            </Button>
          </div>
        </div>
      )}

      {/* Modal Selector de Perfil */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)',
            color: '#ffffff',
            borderBottom: '2px solid #f59e0b',
          }}
        >
          <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
            <span>👁️</span>
            <div>
              <div>Modo Super Cuenta — Ver como cualquier Perfil</div>
              <div className="text-warning fw-normal" style={{ fontSize: '0.75rem' }}>
                Super Cuenta Admin · Entra sin clave y sin desconectar al usuario real
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-light p-3">
          {activeImpersonation && (
            <div className="alert alert-warning d-flex align-items-center justify-content-between py-2 px-3 mb-3 shadow-sm">
              <div>
                <strong>Actualmente viendo como:</strong>{' '}
                {user?.impersonation_label || user?.name || user?.email}
              </div>
              <Button
                size="sm"
                variant="dark"
                className="fw-bold"
                onClick={handleReturnToSuperAdmin}
              >
                🔙 Volver a mi Cuenta Admin
              </Button>
            </div>
          )}

          <Nav
            variant="pills"
            className="mb-3 bg-white p-1 rounded border shadow-sm nav-fill"
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k || 'providers');
              setSearchQuery('');
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey="providers" className="fw-bold py-2">
                🏢 Proveedores ({targets.providers.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="drivers" className="fw-bold py-2">
                🚗 Conductores ({targets.drivers.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="clients" className="fw-bold py-2">
                👔 Clientes B2B ({targets.clients.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="users" className="fw-bold py-2">
                🎧 Usuarios ({targets.users.length})
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <InputGroup className="mb-3 shadow-sm">
            <InputGroup.Text className="bg-white border-end-0">🔍</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={
                activeTab === 'providers'
                  ? 'Buscar proveedor por nombre, país, base o moneda (ej: RedGo, Talita, Allen)...'
                  : activeTab === 'drivers'
                  ? 'Buscar conductor por nombre, proveedor, RUT o teléfono...'
                  : activeTab === 'clients'
                  ? 'Buscar empresa B2B, solicitante o correo...'
                  : 'Buscar usuario interno por nombre, rol o correo...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-start-0"
              autoFocus
            />
            {searchQuery && (
              <Button variant="outline-secondary" onClick={() => setSearchQuery('')}>
                ✕
              </Button>
            )}
          </InputGroup>

          {loadingTargets ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted fw-semibold">Cargando perfiles disponibles...</div>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="text-center py-4 text-muted bg-white rounded border">
              No se encontraron perfiles que coincidan con "{searchQuery}".
            </div>
          ) : (
            <div className="d-flex flex-column gap-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
              {filteredList.map((item) => {
                const key = `${item.target_type}_${item.id}`;
                const isCurrent =
                  activeImpersonation &&
                  user?.impersonation_target_type === item.target_type &&
                  Number(user?.impersonation_target_id) === Number(item.id);

                return (
                  <div
                    key={key}
                    className={`p-3 rounded bg-white border d-flex align-items-center justify-content-between shadow-sm ${
                      isCurrent ? 'border-warning border-2' : ''
                    }`}
                  >
                    <div className="me-3">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-bold text-dark" style={{ fontSize: '0.96rem' }}>
                          {item.name}
                        </span>
                        {item.badge && (
                          <Badge bg="secondary" className="fw-semibold">
                            {item.badge}
                          </Badge>
                        )}
                        {isCurrent && (
                          <Badge bg="warning" text="dark">
                             Viendo ahora
                          </Badge>
                        )}
                      </div>
                      {item.subtitle && (
                        <div className="text-muted small mt-1">{item.subtitle}</div>
                      )}
                    </div>

                    <Button
                      variant={isCurrent ? 'outline-dark' : 'primary'}
                      size="sm"
                      className="fw-bold px-3 text-nowrap shadow-sm"
                      disabled={switchingId === key}
                      onClick={() => handleSelectTarget(item)}
                    >
                      {switchingId === key ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Entrando...
                        </>
                      ) : (
                        '👁️ Entrar como este perfil →'
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-white d-flex justify-content-between">
          <small className="text-muted">
            🔒 Sesión aislada en memoria: no cierra la sesión del usuario real ni altera tu cuenta Admin.
          </small>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SuperAdminImpersonator;
